import { View } from '../types';

export interface PageMeta {
  title: string;
  description: string;
  keywords?: string;
}

export const defaultPageMeta: PageMeta = {
  title: 'Scrudix - AI That Runs Your Business | Code, Legal, QA & Strategy',
  description:
    'Scrudix is the AI platform that runs your business — code review, legal analysis, QA, debugging, team dashboards and strategy. Start free.',
  keywords:
    'Scrudix, AI engineering platform, AI that runs your business, AI generator, AI app generator, team dashboard software, AI meeting summarizer, meeting notes AI, code review AI, workflow automation, Scrudix AI',
};

export const pageMetaByView: Partial<Record<View, PageMeta>> = {
  [View.LANDING]: defaultPageMeta,
  [View.LUMI]: {
    title: 'Lumi by Scrudix - Voice-First AI Bubble Assistant',
    description:
      'Meet Lumi, the sentient bubble companion inside Scrudix. Talk naturally with Scrudix native audio and watch real-time conversational responses.',
    keywords:
      'Lumi, Scrudix Lumi, voice AI assistant, Scrudix native audio, conversational AI bubble',
  },
  [View.GENERATOR]: {
    title: 'AI Generator for Product Blueprints and App Workflows | Scrudix',
    description:
      'Use Scrudix AI Generator to turn one prompt into product blueprints, feature lists, workflow logic, tech stack recommendations, and launch-ready implementation plans.',
    keywords:
      'AI generator, AI app generator, product blueprint generator, AI workflow generator, app planning AI, prompt to product, Scrudix generator',
  },
  [View.DEMO]: {
    title: 'AI Meeting Summarizer, Code Review and Workflow Demo | Scrudix',
    description:
      'Explore practical Scrudix workflows for AI meeting summarizer use cases, code review, debugging, legal analysis, team knowledge management, and automated documentation.',
    keywords:
      'AI meeting summarizer demo, AI workflow demo, code review demo, AI debugging demo, live AI demo, meeting notes AI',
  },
  [View.PRICING]: {
    title: 'Scrudix Pricing - Transparent Plans from Free to Enterprise',
    description:
      'Start free on Starter with 500 AI queries per month, scale with Pro at Rs500/month with automatic localized currency display, and move to Enterprise via contact-sales for custom contracts and deployment.',
    keywords:
      'Scrudix pricing, AI tool pricing, developer tools pricing, free AI plan, enterprise AI pricing',
  },
  [View.EXTENSIONS]: {
    title: 'Best VS Code Extensions for Developers, Cursor & Windsurf | Scrudix',
    description:
      'Explore the best VS Code extensions for code quality, Git workflow, docs, frontend preview, customization, and personality, including Scrudix, GitLens, Prettier, ESLint, Error Lens, Live Server, Material Icon Theme, Peacock, and VS Code Pets.',
    keywords:
      'best VS Code extensions, best VS Code extensions for developers, best Cursor extensions, Windsurf extensions, Scrudix extension, GitLens, Prettier VS Code, ESLint VS Code, Error Lens extension, Live Server VS Code, Material Icon Theme, VS Code Pets, Peacock extension',
  },
  [View.DOCUMENTATION]: {
    title: 'Scrudix Documentation - API Reference, Guides & Integrations',
    description:
      'Complete developer documentation including setup guides, API references, integration tutorials, security best practices, and practical workflow examples.',
    keywords:
      'Scrudix docs, Scrudix API, developer documentation, API reference, integration guide, Scrudix setup',
  },
  [View.PRIVACY]: {
    title: 'Scrudix Privacy Policy',
    description: 'Learn how Scrudix collects, uses, secures, and manages your data.',
    keywords: 'Scrudix privacy policy, data protection, GDPR, data security',
  },
  [View.TERMS]: {
    title: 'Scrudix Terms Of Service',
    description: 'Review account responsibilities, acceptable use, and subscription terms.',
    keywords: 'Scrudix terms of service, terms and conditions, acceptable use policy',
  },
  [View.LEGAL]: {
    title: 'Scrudix Legal And Compliance',
    description: 'Compliance posture, legal notices, and governance information for Scrudix.',
    keywords: 'Scrudix legal, compliance, governance, legal notices',
  },
  [View.CONTACT]: {
    title: 'Scrudix Contact And Support',
    description: 'Reach Scrudix sales, support, and legal teams for help and enterprise onboarding.',
    keywords: 'Scrudix contact, Scrudix support, enterprise onboarding, customer support',
  },
  [View.AUTH]: {
    title: 'Scrudix Sign In',
    description: 'Sign in to Scrudix and continue in dashboard, generator, and integrated workflows.',
    keywords: 'Scrudix sign in, Scrudix login, Scrudix account',
  },
  [View.DASHBOARD]: {
    title: 'Scrudix Dashboard',
    description: 'Manage integrations, workflows, and AI operations in the Scrudix dashboard.',
    keywords: 'Scrudix dashboard, AI dashboard, workflow management',
  },
  [View.FEATURES]: {
    title: 'AI Generator, Team Dashboard, Meeting Summarizer and Workflow Tools | Scrudix',
    description:
      'See everything inside Scrudix: AI generator flows, team dashboard software, meeting summarizer tools, documentation, debugging, code review, and automation systems.',
    keywords:
      'AI generator, team dashboard, meeting summarizer, workflow automation, code review AI, AI documentation tools, Scrudix features',
  },
  [View.HOW_IT_WORKS]: {
    title: 'How Scrudix Works: AI Generator, Team Dashboard and Automation',
    description:
      'Learn how Scrudix turns prompts into workflows, connects team dashboards, summarizes meetings, and helps teams ship with AI-assisted analysis, docs, and debugging.',
    keywords:
      'how AI generator works, team dashboard software, AI meeting summarizer, workflow automation platform, Scrudix how it works',
  },
  [View.COMMANDS]: {
    title: 'Scrudix Terminal Commands - Complete Developer Reference',
    description:
      'Full reference of all Scrudix terminal commands, keyboard shortcuts, aliases, and AI-powered workflows. Master your development workflow with Scrudix CLI.',
    keywords:
      'Scrudix commands, terminal commands, CLI reference, developer tools, keyboard shortcuts, Scrudix terminal',
  },
  [View.HIGGSFIELD]: {
    title: 'Scrudix x HiggsField - Cinematic AI Launch Experience',
    description:
      'Explore the Scrudix x HiggsField collaboration page with a motion-led video backdrop, centered brand reveal, and a sticky chat-driven storytelling experience.',
    keywords:
      'Scrudix x HiggsField, cinematic AI page, video background landing page, motion design, scroll storytelling, premium AI experience',
  },
  [View.STUDIO]: {
    title: 'Scrudix Studio - Prompt to Preview, Blueprint, Code, and Execution',
    description:
      'Build AI-powered products in Scrudix Studio with a modern prompt-to-preview workspace, generated blueprints, code, and execution plans.',
    keywords:
      'Scrudix Studio, AI app builder, prompt to UI, AI product builder, generated blueprint, live preview, code generation, execution plan',
  },
  [View.COMMUNITY]: {
    title: 'Scrudix AI Community - Connect, Share, and Learn',
    description:
      'Join the official Scrudix AI community forum to discuss AI engineering workflows, share custom prompt blueprints, explore trending topics, connect with top builders, and learn how to run your business with AI.',
    keywords:
      'Scrudix AI, Scrudix community, developer community, AI engineering forum, prompt engineering, Scrudix AI builder, AI workflows, community forum, Scrudix forum',
  },
  [View.WORKCLOCK]: {
    title: 'Work Clock, Shift Tracking and Team Time Dashboard | Scrudix',
    description:
      'Track work clocks, shifts, team time, productivity signals, and operating cadence with Scrudix work clock and workforce dashboard tools.',
    keywords:
      'work clock software, shift tracking, team time dashboard, workforce dashboard, productivity tracking, Scrudix work clock',
  },
  [View.TEAM_DASHBOARD]: {
    title: 'Team Dashboard Software for Engineering and Product Teams | Scrudix',
    description:
      'Use Scrudix team dashboard software to track budgets, project quality, team performance, work clocks, delivery signals, and collaboration from one shared workspace.',
    keywords:
      'team dashboard software, engineering dashboard, project dashboard, productivity dashboard, budget dashboard, Scrudix team dashboard',
  },
  [View.NOT_FOUND]: {
    title: 'Page Not Found - Scrudix',
    description:
      'The page you are looking for does not exist or has been moved. Go back to the Scrudix homepage or explore our features, pricing, and documentation.',
    keywords: 'Scrudix, page not found, 404, AI platform',
  },
  [View.BLOG]: {
    title: 'Scrudix Blog - AI Engineering, Code Review & Business Automation',
    description:
      'Expert insights on AI-powered code review, business automation, team dashboards, debugging, and engineering productivity. Learn how to build faster with AI.',
    keywords:
      'AI engineering blog, code review AI, business automation, team dashboard, AI debugging, engineering productivity, Scrudix blog',
  },
  [View.BLOG_POST]: {
    title: 'Blog Post | Scrudix',
    description:
      'Read the latest insights on AI engineering, code review, business automation, and team productivity from the Scrudix blog.',
    keywords: 'AI engineering, code review, business automation, Scrudix',
  },
  [View.COMPARISON]: {
    title: 'Comparison | Scrudix',
    description:
      'Compare Scrudix with other AI coding and app building platforms. See features, pricing, pros, cons, and find the best tool for your team.',
    keywords: 'Scrudix comparison, AI coding tools comparison, Scrudix vs, AI platform comparison',
  },
};
